import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../../../../../../../@core/services/toast.service';
import { ScoringService } from '../../../../../../../../../../@shared/services/scoring.service';
import {
  EventClassResponse,
  ClassScoringConfigResponse,
  ScoreTypeName
} from '../../../../../../../../../../@shared/models/response.module';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-scoring-config-modal',
  standalone: false,
  templateUrl: './scoring-config-modal.html',
  styleUrl: './scoring-config-modal.css'
})
export class ScoringConfigModal implements OnInit {
  @Input() eventClass: EventClassResponse;

  form: FormGroup;
  saving = false;
  loading = true;
  existingConfig: ClassScoringConfigResponse | null = null;

  scoreTypeOptions = [
    { value: 'timed', labelKey: 'scoring.timed' },
    { value: 'points_high', labelKey: 'scoring.pointsHigh' },
    { value: 'points_low', labelKey: 'scoring.pointsLow' },
    { value: 'combined', labelKey: 'scoring.combined' }
  ];

  penaltyTypeOptions = [
    { value: 'time', labelKey: 'scoring.timeBased' },
    { value: 'points', labelKey: 'scoring.pointsBased' }
  ];

  constructor(
    private fb: FormBuilder,
    private modal: NgbActiveModal,
    private scoringService: ScoringService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {
    this.form = this.fb.group({
      score_type: ['timed', Validators.required],
      penalty_type: ['time', Validators.required],
      time_weight: [1],
      points_weight: [1],
      decimal_places: [2]
    });
  }

  ngOnInit() {
    this.loadExistingConfig();
  }

  async loadExistingConfig() {
    this.loading = true;
    try {
      const config = await firstValueFrom(
        this.scoringService.getScoringConfig({ event_class_id: this.eventClass.event_class_id })
      );
      if (config) {
        this.existingConfig = config;
        this.form.patchValue({
          score_type: config.score_type,
          penalty_type: config.penalty_type,
          time_weight: config.time_weight || 1,
          points_weight: config.points_weight || 1,
          decimal_places: config.decimal_places || 2
        });
      }
    } catch (error) {
      // No existing config, use defaults
    } finally {
      this.loading = false;
    }
  }

  get isCombinedScoring(): boolean {
    return this.form.get('score_type')?.value === 'combined';
  }

  async save() {
    if (this.form.invalid || this.saving) return;

    this.saving = true;
    try {
      const formValue = this.form.value;
      await firstValueFrom(
        this.scoringService.saveScoringConfig({
          event_class_id: this.eventClass.event_class_id,
          score_type: formValue.score_type,
          penalty_type: formValue.penalty_type,
          time_weight: formValue.time_weight,
          points_weight: formValue.points_weight,
          decimal_places: formValue.decimal_places
        })
      );
      this.toastService.success(this.translateService.instant('scoring.savedSuccess'));
      this.modal.close(true);
    } catch (error) {
      console.error('Error saving scoring config:', error);
      this.toastService.error(this.translateService.instant('scoring.saveError'));
    } finally {
      this.saving = false;
    }
  }

  close() {
    this.modal.dismiss();
  }
}
